import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';

/*
  A movement run, from the first photograph to the record left behind.

  Written 8 September 2026 for "Build the Ordering area". **Until today nothing
  Regional Product Movement did was saved at all**: the photographs, the lines
  read off them, every correction somebody made, and the weekly reorder lived in
  one browser tab for one sitting. Close the tab halfway through and the
  morning's work was gone, with nothing said.

  -- The owner's rule, which this file carries out --------------------------

  5 September 2026: *"The picture should not stay in the repository, the images
  should be uploaded the process ran and once the process is complete and the
  review done, the images should be deleted. But I do want a history of th items
  date the process was ran etc kept in the database with supabase."*

  So a run goes: **start**, **upload each page**, **read them**, **review**, then
  **finish**, and finishing is what deletes the photographs. What stays behind
  is the run and its lines, which is what Run History shows.

  -- The one order that matters --------------------------------------------

  **The files are removed BEFORE the record says they are gone**, and never the
  other way round. A record saying "deleted 8 Sep" over files still sitting in
  storage is a lie nobody would ever catch, because nothing would look at them
  again. A file left behind after a failed removal is recoverable: its path is
  still on the row and trying again works.
*/

const BUCKET = 'movement-pages';

/* One page's home: the company, then the run, then the file. The company folder
   comes first because the storage rules read it to decide who may look. */
const pathFor = (orgId, runId, pageNo, name) =>
  `${orgId}/${runId}/${String(pageNo).padStart(3, '0')}-${(name || 'page').replace(/[^\w.-]+/g, '_')}`;

/*
  The name a run carries on the list: the territory and the day, like
  "LOU 2026-09-08", which is what the approved drawing shows.
*/
export function runName(territory, when = new Date()) {
  const d = [when.getFullYear(), String(when.getMonth() + 1).padStart(2, '0'),
             String(when.getDate()).padStart(2, '0')].join('-');
  return ((territory || '').trim() ? territory.trim().toUpperCase() + ' ' : '') + d;
}

/*
  **The territory is read off the warehouse the movement ships from**, which is
  the prefix in front of its name: LOU-MAIN is LOU, ETX-TYLER is ETX.

  The owner said on 4 September 2026 that Regional Product Movement is Louisiana
  AND East Texas, so the `LOU-MAIN` typed into the tool's defaults is wrong for
  half of it. Taking the territory from what was actually picked means a run
  never claims to be in a region it was not.
*/
export function territoryOf(warehouseName) {
  const m = /^([A-Za-z]{2,5})-/.exec((warehouseName || '').trim());
  return m ? m[1].toUpperCase() : null;
}

export async function startRun({ shipsFrom, profile }) {
  const { data: orgId, error: orgErr } = await supabase.rpc('current_org_id');
  if (orgErr) return { run: null, error: orgErr };
  const territory = territoryOf(shipsFrom);
  const { data, error } = await supabase.from('movement_runs').insert({
    org_id: orgId,
    name: runName(territory),
    territory,
    ships_from: shipsFrom || null,
    status: 'reading',
    ran_by: profile?.id || null,
    ran_by_name: profile?.full_name || null,
  }).select('id, org_id, name, territory, ships_from, ran_at').single();
  return { run: data || null, error };
}

/*
  One photograph, into storage and onto the run.

  **A page row is only written once the file is actually up there.** The other
  order would leave a row promising a picture that is not there, and the run
  would later hand a path to be deleted that never existed.
*/
export async function uploadPage({ run, file, pageNo }) {
  const path = pathFor(run.org_id, run.id, pageNo, file?.name);
  const { error: upErr } = await supabase.storage.from(BUCKET)
    .upload(path, file, { upsert: true, contentType: file?.type || 'image/jpeg' });
  if (upErr) return { page: null, error: upErr };

  const { data, error } = await supabase.from('movement_run_pages').insert({
    run_id: run.id, org_id: run.org_id,
    page_no: pageNo, file_name: file?.name || null, storage_path: path,
  }).select('id, page_no, file_name, storage_path').single();
  return { page: data || null, error };
}

export async function markPageRead(pageId, lineCount) {
  const { error } = await supabase.from('movement_run_pages')
    .update({ read_at: new Date().toISOString(), line_count: lineCount || 0 })
    .eq('id', pageId);
  return { error };
}

export async function markRunReading(runId, pageCount) {
  const { error } = await supabase.from('movement_runs')
    .update({ status: 'review', page_count: pageCount || 0, updated_at: new Date().toISOString() })
    .eq('id', runId);
  return { error };
}

/*
  **The lines are written when the pages are READ, not when the file is made.**
  Corrected 8 September 2026, a few hours after the first version.

  The first version saved them at export. That left the middle of a run
  unprotected: the photographs were in storage and the run existed, but
  everything the handwriting reader had found lived in one browser tab. Close it
  between reading and exporting, which is exactly where somebody goes to lunch,
  and the morning's work was still gone. **That is the fault this whole piece
  exists to fix**, so half fixing it was worse than not noticing.

  So they are saved twice: once when the pages are read, with what would happen
  to each line as it stands, and again when the file is made, with what actually
  did. `replaceRunLines` is used both times.

  The lines, with what happened to each.

  **Three outcomes rather than a yes or no**, which is the approved drawing's
  last column. A line held back is kept and says why, because a history that
  dropped those would report a run as cleaner than it was. And a line that went
  in with a unit beside its quantity is marked, because months later, with the
  photograph gone, this is the only place anybody could find out that a case of
  twenty left OnHand as a one.
*/
export async function saveRunLines({ run, rows, fileName }) {
  /*
    **Replaced, not added to.** A run is read once and exported once, and the
    second write is the same lines with their outcomes settled. Adding would
    double every line and the history would report twice the work.
  */
  const { error: clearErr } = await supabase.from('movement_run_lines')
    .delete().eq('run_id', run.id);
  if (clearErr) return { error: clearErr };
  if (!rows.length) return { error: null };
  const payload = rows.map((r, i) => ({
    run_id: run.id, org_id: run.org_id,
    page_id: r.pageId || null,
    line_no: i + 1,
    written: r.written || null,
    /*
      **The date somebody wrote on the page**, kept as written. Found missing on
      8 September 2026 by photographing Review with a run picked back up: every
      Date box on the screen was empty, and the file made afterwards would have
      had no dates in its Comments column at all.
    */
    written_date: r.date || null,
    destination: r.destination || null,
    item_number: r.itemNumber || null,
    description: r.description || null,
    quantity: r.quantity === '' || r.quantity === null || r.quantity === undefined ? null : Number(r.quantity),
    unit: r.unit || null,
    outcome: r.outcome,
    why: r.why || null,
  }));

  let written = 0;
  for (let i = 0; i < payload.length; i += 200) {
    const { data, error } = await supabase.from('movement_run_lines')
      .insert(payload.slice(i, i + 200)).select('id');
    if (error) return { error };
    written += (data || []).length;
  }

  const { error } = await supabase.from('movement_runs').update({
    line_count: written,
    location_count: locationsIn(rows.map(r => ({ destination: r.destination }))),
    file_name: fileName || null,
    updated_at: new Date().toISOString(),
  }).eq('id', run.id);
  return { error };
}

/*
  Finishing a run, which is the moment the photographs go.

  **The files first, then the record.** The database hands back every path it
  holds, this removes them, and only then does the run say the pictures are
  gone. If the removal fails the paths are still on their rows, so trying again
  works and nothing is silently orphaned.
*/
export async function finishRun(runId) {
  const { data: paths, error } = await supabase.rpc('movement_run_finish', { target_run: runId });
  if (error) return { removed: 0, error };
  const list = (paths || []).map(p => p.path || p).filter(Boolean);
  if (!list.length) return { removed: 0, error: null };
  const { error: rmErr } = await supabase.storage.from(BUCKET).remove(list);
  return { removed: rmErr ? 0 : list.length, error: rmErr || null };
}

/*
  A run this company started and has not finished.

  **So a closed tab is no longer the end of a morning's work.** The pages are in
  storage and their lines are in the database from the moment they are read, so
  picking the run back up is a read rather than a re-run of the handwriting
  reader, which costs about three cents a page.

  The newest one, because two unfinished runs at once means somebody started
  again and the older one is the abandoned attempt.
*/
export async function unfinishedRun() {
  const { data, error } = await supabase
    .from('movement_runs')
    .select('id, org_id, name, territory, ships_from, ran_at, page_count, status')
    .in('status', ['reading', 'review'])
    .order('ran_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { run: data || null, error };
}

/* -- Reading the history back ------------------------------------------- */

export async function listRuns() {
  const { data, error } = await fetchAll(() => supabase
    .from('movement_runs')
    .select('id, name, territory, ran_by_name, ran_at, page_count, line_count, location_count, photos_deleted_at, status, file_name')
    .order('ran_at', { ascending: false }));
  return { runs: data || [], error };
}

export async function getRun(runId) {
  const [{ data: run, error: runErr }, { data: lines, error: lineErr }] = await Promise.all([
    supabase.from('movement_runs').select('*').eq('id', runId).maybeSingle(),
    fetchAll(() => supabase.from('movement_run_lines')
      .select('id, line_no, written, written_date, destination, item_number, description, quantity, unit, outcome, why')
      .eq('run_id', runId).order('line_no')),
  ]);
  return { run: run || null, lines: lines || [], error: runErr || lineErr };
}

/*
  **How many places a run touched.** The drawing's third figure. It is counted
  off the lines rather than stored, because unlike the pages the lines are never
  deleted, so this can never go stale.
*/
export function locationsIn(lines) {
  return new Set(lines.map(l => l.destination).filter(Boolean)).size;
}
